import { useEffect, useState } from 'react'

import {
  EmptyState,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { ReportsRecords } from 'models/PaymentLink'
import { ProductLinkService } from 'services/ProductLink'

import IconContainer from 'components/IconContainer/IconContainer'
import { Table } from 'components/Table'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import ModalChargeDetail from '../ModalChargeDetail'
import ModalRecurrenceDetail from '../ModalRecurrenceDetail'
import Info from './components/CardInfo/Info'
import Loading from './components/Loading'
import * as S from './ModalPaymentLinkReport.styles'
import { convertToRows } from './ModalPaymentLinkReport.utils'
import ReportChargeFilters from './ReportChargeFilters'
import ReportRecurrenceFilters from './ReportRecurrenceFilters'

// Mesmo formato que os filtros esperam (`{ [key]: string }`), para continuar
// compatível com `browserQueryToQueryString` e `handleDeleteQueryFilters`.
type ReportFilters = { [key in string]: string }

type ModalPaymentLinkReportProps = {
  uuid: string
  handleCloseModalPaymentReport: (value: boolean) => void
  isOpenModalPaymentReport: boolean
}
export type dataStateProps = {
  meta: MetaProps | null
  data: ReportsRecords | null
}
type MetaProps = {
  current_page: number
  next_page: number
  prev_page: number
  total_count: number
  total_pages: number
}

const ModalPaymentLinkReport = ({
  uuid,
  handleCloseModalPaymentReport,
  isOpenModalPaymentReport
}: ModalPaymentLinkReportProps) => {
  // Filtros do relatório vivem em estado local, NÃO na query da URL. A página
  // por trás do modal (lista de links) filtra pelas MESMAS chaves — status,
  // sort, page, per — e escrever na URL daqui apagava o filtro da lista.
  const [reportQuery, setReportQuery] = useState<ReportFilters>({})

  // Os filtros chamam `setBaseFilters({ query })` — mesma assinatura que o
  // `push` do router tinha. Aqui só normalizamos os valores para string.
  const setBaseFilters = (params: {
    [key in string]: string | number | object
  }) => {
    const next = (params?.query ?? {}) as Record<string, unknown>
    setReportQuery(
      Object.fromEntries(
        Object.entries(next)
          .filter(([, value]) => value !== undefined && value !== null)
          .map(([key, value]) => [
            key,
            Array.isArray(value) ? value.join('|') : String(value)
          ])
      )
    )
  }

  const [dataState, setDataState] = useState<dataStateProps>({
    meta: null,
    data: null
  })

  const [modals, setModals] = useState({
    charge: {
      open: false,
      uuid: ''
    },
    recurrence: {
      open: false,
      uuid: ''
    }
  })

  const handleOpenRecurrenceDetails = (uuid: string) => {
    setModals((s) => ({
      ...s,
      recurrence: {
        open: true,
        uuid
      }
    }))
  }

  const handleOpenChargeDetails = (chargeUiid: string) => {
    setModals((s) => ({
      ...s,
      charge: {
        open: true,
        uuid: chargeUiid
      }
    }))
  }

  const handleChangePage = (page: number) => {
    setReportQuery((prev) => ({ ...prev, page: String(page) }))
  }

  const handleChangeRowsPerPage = (row: string) => {
    setReportQuery((prev) => ({
      ...prev,
      per: String(parseInt(row)),
      page: '0'
    }))
  }

  const updateQueryForTable = () => {
    getLinkReports(uuid)
  }

  const getLinkReports = async (uuid: string) => {
    const response = await ProductLinkService.getReports(uuid, reportQuery)

    setDataState({
      meta: response?.data.meta,
      data: response?.data?.record
    })
  }

  // Cada abertura começa com o relatório sem filtro, que era o efeito de
  // `handleClearFilters()` no mount dos filtros — agora sem mexer na URL.
  useEffect(() => {
    if (isOpenModalPaymentReport) {
      setReportQuery({})
    }
  }, [isOpenModalPaymentReport, uuid])

  useEffect(() => {
    if (isOpenModalPaymentReport) {
      getLinkReports(uuid)
    }
  }, [uuid, reportQuery, isOpenModalPaymentReport])

  return (
    <Modal
      handleClose={() => {
        handleCloseModalPaymentReport(false)
        setDataState({ ...dataState, data: null, meta: null })
      }}
      open={isOpenModalPaymentReport}
      maxWidth="900px"
      width="100% !important"
      height="85%"
      maxHeight="85vh"
      disablePortal={false}
    >
      {dataState?.data ? (
        <ModalBody>
          <ModalHeader
            onClose={() => {
              handleCloseModalPaymentReport(false)
              setDataState({ ...dataState, data: null, meta: null })
            }}
          >
            <Stack direction="row" gap={2} alignItems="center">
              <IconContainer icon="barChart" />
              <Typography variant="mdBold">Relatório</Typography>
            </Stack>
          </ModalHeader>

          <ModalContent>
            <Box>
              <S.AmountWrapper>
                <S.AmountContent>
                  <Typography variant="smLight">Total em aberto:</Typography>
                  <Typography fontSize="20px" variant="smBold">
                    {formatMoney(dataState?.data?.total_to_received)}
                  </Typography>
                </S.AmountContent>
                <S.Divider
                  sx={{ alignSelf: 'center', justifySelf: 'center' }}
                />
                <S.AmountContent
                  sx={{
                    [theme.breakpoints.down(560)]: {
                      gridColumn: '2',
                      gridRow: '1'
                    },
                    justifySelf: 'end'
                  }}
                >
                  <Typography variant="smLight">Total pago:</Typography>
                  <Typography fontSize="20px" variant="smBold">
                    {formatMoney(dataState?.data?.total_received)}
                  </Typography>
                </S.AmountContent>
              </S.AmountWrapper>

              <S.DetailContent>
                <Info dataState={dataState} />
                <S.DetailWrapper
                  sx={{
                    [theme.breakpoints.down(560)]: {
                      maxWidth: '100%',
                      width: 'auto',
                      display: 'flex'
                    }
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="12px"
                    width="100%"
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      flexWrap="wrap"
                      gap="8px"
                      sx={{
                        [theme.breakpoints.down(560)]: {
                          width: '100%',
                          alignItems: 'center',
                          display: 'grid',
                          justifyContent: 'space-between'
                        }
                      }}
                    >
                      <Typography variant="smLight">
                        {dataState?.data?.charge_type === 'single'
                          ? 'Cobranças'
                          : 'Recorrências'}
                      </Typography>
                      <Typography
                        mr="auto"
                        gridRow="1"
                        sx={{
                          [theme.breakpoints.down(560)]: {
                            gridColumn: '2',
                            gridRow: '0'
                          }
                        }}
                        fontSize="20px"
                        variant="smBold"
                      >
                        {dataState?.data?.total_charges}
                      </Typography>
                    </Box>
                    <Box width="100%">
                      {dataState?.data?.product?.charge_type === 'recurrent' ? (
                        <ReportRecurrenceFilters
                          baseFilters={reportQuery}
                          setBaseFilters={setBaseFilters}
                        />
                      ) : (
                        <ReportChargeFilters
                          baseFilters={reportQuery}
                          setBaseFilters={setBaseFilters}
                        />
                      )}
                    </Box>
                  </Box>
                  {dataState.data ? (
                    <Grid
                      sx={{
                        [theme.breakpoints.down(560)]: {
                          width: 'auto'
                        }
                      }}
                    >
                      {!dataState?.data?.charges.length ? (
                        <Box m={4}>
                          <EmptyState
                            title="Nenhum dado foi encontrado"
                            message="Quando a primeira venda for efetuada por esse Link, os dados ficarão disponíveis aqui."
                          />
                        </Box>
                      ) : (
                        <Table
                          mobileCards
                          header={[
                            { id: 'name', label: 'Cliente' },
                            { id: 'dueDate', label: 'Vencimento' },
                            { id: 'amountBilled', label: 'Valor' },
                            { id: 'status', label: 'Status' },
                            { id: 'actions', label: '' }
                          ]}
                          rows={convertToRows(dataState?.data?.charges || [], {
                            handleOpenRecurrenceDetails,
                            handleOpenChargeDetails
                          })}
                          select={{ useSelect: false }}
                          page={
                            dataState.meta
                              ? ((dataState.meta?.current_page - 1) as number)
                              : 1
                          }
                          rowsPerPage={Number(reportQuery?.per) || 25}
                          totalCount={dataState.meta?.total_count as number}
                          handleChangePage={(_e, newPage) =>
                            handleChangePage(newPage)
                          }
                          handleChangeRowsPerPage={(e) =>
                            handleChangeRowsPerPage(e.target.value)
                          }
                          hiddePagination={dataState.meta?.total_count === 0}
                        />
                      )}
                    </Grid>
                  ) : null}
                </S.DetailWrapper>
              </S.DetailContent>
            </Box>
          </ModalContent>
        </ModalBody>
      ) : (
        <Loading />
      )}

      {modals.charge.open && (
        <ModalChargeDetail
          handleCloseChargeDetail={() =>
            setModals((s) => ({
              ...s,
              charge: {
                open: false,
                uuid: ''
              }
            }))
          }
          isOpenChargeDetail={modals.charge.open}
          uuid={modals.charge.uuid}
          setReloadTable={updateQueryForTable}
          zIndex={999999999}
        />
      )}

      {/* Não da para adaptar */}
      {modals.recurrence.open && (
        <ModalRecurrenceDetail
          handleCloseDetailRecurrence={() =>
            setModals((s) => ({
              ...s,
              recurrence: {
                open: false,
                uuid: ''
              }
            }))
          }
          isOpenModalRecurrenceDetail={modals.recurrence.open}
          uuid={modals.recurrence.uuid}
          reloadTable={updateQueryForTable}
        />
      )}
    </Modal>
  )
}

export default ModalPaymentLinkReport
